/* @flow */
/* eslint-env es6,browser */

import { isJSONSupported } from '../utils/browserutil';
import bidbase from './bidbase';
import { isFetchSupported, fetchJSON } from '../utils/fetch';
import { logError } from '../utils/logger';
import { extend, param } from '../utils/coreutils';
import { allSettledPromise } from '../utils/promiseutils';

/**
 * @module bidders/adtech
 * @requires module:logger
 * @requires module:coreutils
 * @requires module:promiseutils
 */
/**
 * @global
 * @typedef {object} adtechConfig
 * @description extends {@link hbConfig}
 * @property {string} bidKey
 * @property {string} id
 * @property {string} server
 */
/**
 * @description Implements functionality for adtech header bidding
 * @augments _bidbase
 * @requires module:utils
 */
class _adtechbid extends bidbase {
  _config: adtechConfig;

  _DefConfig: adtechConfig;

  _bidResponses: { [key: string]: RTBBidResponse };

  constructor(name: string = 'adtech') {
    super(name);
    this._DefConfig = extend(true, {}, this._DefConfig, {
      id: '',
      server: '',
      sspServer: '',
      sspDcn: '',
      useSSP: false
    });
    this._config = extend(true, {}, this._DefConfig);
    this._mandatoryConfigs = ['id', 'server'];
    this._isSupported = isJSONSupported() && isFetchSupported();
  }

  /**
   * Process RTB bid response.
   *
   * @typedef {object} hbPosData
   * @param {Array<string>} posList - array of positions
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {object.<string, hbPosData>} - Return data
   * @access private
   */
  _processBidResponse(
    posList: Array<string>,
    ret: hbPartnerData
  ): hbPartnerData {
    posList.forEach(pos => {
      const resp: RTBBidResponse = this._bidResponses[pos];
      if (!this._isValidRTBResponse(resp)) {
        return;
      }
      // eslint-disable-next-line prefer-destructuring
      const res = resp.seatbid[0].bid[0];
      if (res && res.price && res.adm) {
        const { impid = '', id = '' } = res;
        this._handleBidPosData(
          ret,
          pos,
          res.price,
          impid.length ? impid : id,
          res.adm
        );
      }
      delete this._bidResponses[pos];
    });
    return ret;
  }

  _getPosReqUrl(pos: string = ''): string {
    let url: string = '';
    if (!pos.length) {
      return url;
    }
    const defPositionData = {
      network: this._config.id,
      placement: 0,
      pageid: 0,
      sizeid: 0,
      alias: '',
      misc: Date.now(),
      dynamicParams: ''
    };
    const posData = extend(
      true,
      {},
      defPositionData,
      this._config.posTokenMap[pos]
    );
    posData.dynamicParams = param(this._getPrivacyString());

    if (
      this._config.useSSP &&
      this._config.sspServer.length > 0 &&
      this._config.sspDcn.length > 0 &&
      posData.sspPos
    ) {
      const sspParams = {
        dcn: this._config.sspDcn,
        pos: posData.sspPos,
        cmd: 'bid'
      };
      /* Use SSP Server (with scheme) */
      url = `${this._config.sspServer}/bidRequest?${param(sspParams)}&${
        posData.dynamicParams
      }`;
    } else {
      url = `//${this._config.server}/pubapi/3.0/${posData.network}/${parseInt(
        posData.placement,
        10
      )}/${posData.pageid}/${
        posData.sizeid
      }/ADTECH;v=2;cmd=bid;cors=yes;alias=${posData.alias};misc=${
        posData.misc
      };${posData.dynamicParams}`;
    }
    return url;
  }

  _getBid(pos: string): Promise<boolean> {
    const url = this._getPosReqUrl(pos);
    const opt = {
      data: {},
      timeout: this._config.timeout,
      logPrefix: pos
    };
    const req = {
      method: 'GET',
      credentials: 'include'
    };
    return fetchJSON(url, opt, req).then((json: RTBBidResponse) => {
      this._bidResponses[pos] = json;
      return true;
    });
  }

  /**
   * get bids for given positions
   *
   * @param {Array<string>} posList - HB position list
   * @param {object.<string, hbPosData>} ret - return data
   * @returns {Promise<object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    this._bidResponses = {};
    // Create promise array with all bid requests
    const pArray = posList.map(pos => this._getBid(pos));
    return allSettledPromise(pArray).then(
      arr => {
        const fValues = arr
          .filter(v => v.state !== 'fulfilled')
          .map(v => v.reason);
        if (fValues.length) {
          // Log all failed promise errors
          logError(`${this._name} bidding errors: ${fValues.join(',')}`);
        }
        return this._processBidResponse(posList, ret);
      },
      error => {
        logError(`${this._name} bidding failed: ${error || ''}`);
        return ret;
      }
    );
  }
}

const adtechbid = new _adtechbid();
export default adtechbid;
